
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { TrackingContext } from 'context/Tracking';
import { UserContext } from 'context/User';
import { useEffectOnce } from 'hooks/useEffectOnce';
import { LoadingInstanceLocationState } from 'pages/SignUp/types';
import { resolveInstanceUrl } from 'utils/resolveInstanceUrl';
import SmartInterval from 'utils/SmartInterval';
import { useHistory, useLocationState } from 'utils/router';

export const useCheckInstanceLoaded = () => {
  const [instanceUrls, setInstanceUrls] = useState({ instanceUrl: '', returnUrl: '' });
  const [{ userData }] = useContext(UserContext);

  const [, { faro, trackEvent }] = useContext(TrackingContext);
  const [redirectTo, setRedirectTo] = useState<string>('');
  const { search } = useLocation();
  const locationState = useLocationState<LoadingInstanceLocationState>();
  const history = useHistory();
  const params = new URLSearchParams(search);
  const instanceUrl = params?.get('instanceUrl');
  const returnUrl = params?.get('url');
  const stackReady = locationState?.stackReady === true;

  const checkInstanceReady = useCallback(async (): Promise<boolean> => {
    if (instanceUrls.instanceUrl) {
      return fetch(resolveInstanceUrl(instanceUrls.instanceUrl, '/api/health'))
        .then((response) => {
          return response.ok;
        })
        .catch((err) => {
          console.log('err', err.message);
          return false;
        });
    }
    return Promise.resolve(false);
  }, [instanceUrls.instanceUrl]);

  useEffectOnce(() => {
    if (!!instanceUrl && !!returnUrl) {
      setInstanceUrls({ instanceUrl, returnUrl });

      params.delete('instanceUrl');
      params.delete('url');
      history.replace({ search: params.toString() }, locationState);
    }
  });

  useEffect(() => {
    let dataFetcher: any;

    if (!redirectTo && instanceUrls.instanceUrl && instanceUrls.returnUrl) {
      const redirect = resolveInstanceUrl(
        instanceUrls.instanceUrl,
        `/set-redirect-and-login?url=${encodeURIComponent(instanceUrls.returnUrl)}`
      );

      const completeRedirect = (eventType: 'user-hg-instance-ready' | 'user-hg-instance-reached-max-retries') => {
        faro.trackEvent(eventType, { redirect });
        trackEvent({
          name: eventType,
          userId: userData?.id,
          redirect,
        });
        setRedirectTo(redirect);
      };

      if (stackReady) {
        completeRedirect('user-hg-instance-ready');
        return;
      }

      // retry up to 500 times to check the instance every 1.5 seconds. If no success after 500 retries, redirect to portal
      let totalRetry = 500;
      let retries = 0;

      dataFetcher = new SmartInterval(async () => {
        retries++;
        const isReady = await checkInstanceReady();

        if (isReady || retries >= totalRetry) {
          dataFetcher.stop();
          completeRedirect(isReady ? 'user-hg-instance-ready' : 'user-hg-instance-reached-max-retries');
        }
      }, 1500);
      dataFetcher.start();
    }
    // Clearing the interval
    return () => {
      if (dataFetcher) {
        dataFetcher.stop();
      }
    };
  }, [checkInstanceReady, instanceUrls, redirectTo, faro, trackEvent, userData, stackReady]);

  return redirectTo;
};
